"use client";

import React, { createContext, useCallback, useContext, useState } from "react";

interface Settings {
  layout: string;
  container: "fixed" | "fluid";
  layouts: {
    demo1: { sidebarCollapse: boolean };
  };
}

const defaults: Settings = {
  layout: "demo1",
  container: "fixed",
  layouts: { demo1: { sidebarCollapse: false } },
};

interface SettingsCtx {
  settings: Settings;
  setOption: (path: string, value: unknown) => void;
  storeOption: (path: string, value: unknown) => void;
}

const SettingsContext = createContext<SettingsCtx>({ settings: defaults, setOption: () => {}, storeOption: () => {} });

function setPath(obj: Record<string, unknown>, path: string, val: unknown) {
  const keys = path.split(".");
  const last = keys.pop()!;
  const target = keys.reduce((a: Record<string, unknown>, k) => (a[k] ??= {}) as Record<string, unknown>, obj);
  target[last] = val;
  return { ...obj } as unknown as Settings;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaults);

  const setOption = useCallback((path: string, value: unknown) => {
    setSettings((prev) => setPath({ ...prev } as unknown as Record<string, unknown>, path, value));
  }, []);

  const storeOption = useCallback((path: string, value: unknown) => {
    setSettings((prev) => {
      const next = setPath({ ...prev } as unknown as Record<string, unknown>, path, value);
      try { localStorage.setItem(`app_settings_${path}`, JSON.stringify(value)); } catch {}
      return next;
    });
  }, []);

  return <SettingsContext.Provider value={{ settings, setOption, storeOption }}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext);
